import { useState } from "react";
import { motion } from "framer-motion";
import { Save, Bell, Mail, Shield, Globe } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { toast } from "sonner";

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    companyName: "Best Disney Transfer",
    email: "support@bestdisneytransfer.com",
    phone: "+33 1 23 45 67 89",
    nightSurcharge: 15,
    freeWaitingTime: 60,
    emailNotifications: true,
    smsNotifications: false,
    autoConfirmBookings: true,
  });

  const handleSave = () => {
    toast.success("Settings saved successfully");
  };

  return (
    <div data-testid="admin-settings">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">Settings</h1>
        <p className="text-slate-600">Manage your application settings</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary-600" />
                Company Information
              </CardTitle>
              <CardDescription>Basic company details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={settings.companyName}
                  onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                />
              </div>
              <div>
                <Label>Support Email</Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Pricing Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-600" />
                Pricing Rules
              </CardTitle>
              <CardDescription>Configure pricing settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Night Surcharge (€)</Label>
                <Input
                  type="number"
                  value={settings.nightSurcharge}
                  onChange={(e) => setSettings({ ...settings, nightSurcharge: parseInt(e.target.value) })}
                />
                <p className="text-sm text-slate-500 mt-1">Applied 10 PM - 6 AM</p>
              </div>
              <div>
                <Label>Free Waiting Time (minutes)</Label>
                <Input
                  type="number"
                  value={settings.freeWaitingTime}
                  onChange={(e) => setSettings({ ...settings, freeWaitingTime: parseInt(e.target.value) })}
                />
                <p className="text-sm text-slate-500 mt-1">For airport pickups</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary-600" />
                Notifications
              </CardTitle>
              <CardDescription>Configure notification preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Email Notifications</p>
                  <p className="text-sm text-slate-500">Receive booking emails</p>
                </div>
                <Switch
                  checked={settings.emailNotifications}
                  onCheckedChange={(c) => setSettings({ ...settings, emailNotifications: c })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">SMS Notifications</p>
                  <p className="text-sm text-slate-500">Receive SMS alerts</p>
                </div>
                <Switch
                  checked={settings.smsNotifications}
                  onCheckedChange={(c) => setSettings({ ...settings, smsNotifications: c })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Auto-confirm Bookings</p>
                  <p className="text-sm text-slate-500">Automatically confirm new bookings</p>
                </div>
                <Switch
                  checked={settings.autoConfirmBookings}
                  onCheckedChange={(c) => setSettings({ ...settings, autoConfirmBookings: c })}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Email Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary-600" />
                Email Integration
              </CardTitle>
              <CardDescription>SendGrid configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>SendGrid API Key</Label>
                <Input
                  type="password"
                  placeholder="SG.xxxxxx..."
                />
                <p className="text-sm text-slate-500 mt-1">Required for email notifications</p>
              </div>
              <div>
                <Label>Admin Email</Label>
                <Input
                  type="email"
                  placeholder="admin@bestdisneytransfer.com"
                />
                <p className="text-sm text-slate-500 mt-1">Receives booking notifications</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} className="bg-primary-600 hover:bg-primary-700">
          <Save className="w-4 h-4 mr-2" />
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
